import { AxiosInstance } from "axios";
import { ArchiConversation, ArchiMessageRow } from "./types";

const BASE = "/chat/archi";

interface Envelope<T> {
  success: boolean;
  data: T;
  message?: string;
}

export async function listConversations(api: AxiosInstance) {
  const res = await api.get<Envelope<ArchiConversation[]>>(`${BASE}/conversations`);
  return res.data.data;
}

export async function getMessages(api: AxiosInstance, id: number) {
  const res = await api.get<
    Envelope<{ conversation: ArchiConversation; messages: ArchiMessageRow[] }>
  >(`${BASE}/conversations/${id}/messages`);
  return res.data.data;
}

export async function deleteConversation(api: AxiosInstance, id: number) {
  await api.delete<Envelope<null>>(`${BASE}/conversations/${id}`);
}

export async function renameConversation(
  api: AxiosInstance,
  id: number,
  title: string
) {
  const res = await api.patch<Envelope<ArchiConversation>>(
    `${BASE}/conversations/${id}`,
    { title }
  );
  return res.data.data;
}

/**
 * SSE stream of an Archi reply.
 *
 * We can't use EventSource because it doesn't allow custom headers
 * (and we need the Auth0 Bearer). Instead we fetch with the same Auth0
 * token, parse the SSE stream manually, and call back per event.
 */
export async function streamMessage(
  opts: {
    token: string;
    apiBaseUrl: string;
    body: {
      conversationId?: number | null;
      message: string;
      currentRoute?: string | null;
    };
    onEvent: (event: string, data: unknown) => void;
    signal?: AbortSignal;
  }
): Promise<void> {
  const { token, apiBaseUrl, body, onEvent, signal } = opts;
  const url = `${apiBaseUrl.replace(/\/$/, "")}/v1/chat/archi/messages`;
  const resp = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "text/event-stream",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
    signal,
  });

  if (!resp.ok || !resp.body) {
    let message = `HTTP ${resp.status}`;
    try {
      const j = await resp.json();
      if (j?.message) message = j.message;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";

  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // SSE frames are separated by a blank line.
    let sepIdx = buffer.indexOf("\n\n");
    while (sepIdx !== -1) {
      const frame = buffer.slice(0, sepIdx);
      buffer = buffer.slice(sepIdx + 2);

      let eventName = "message";
      const dataLines: string[] = [];
      for (const line of frame.split("\n")) {
        if (line.startsWith("event:")) {
          eventName = line.slice(6).trim();
        } else if (line.startsWith("data:")) {
          dataLines.push(line.slice(5).trim());
        }
      }
      if (dataLines.length) {
        try {
          onEvent(eventName, JSON.parse(dataLines.join("\n")));
        } catch {
          onEvent(eventName, { raw: dataLines.join("\n") });
        }
      }
      sepIdx = buffer.indexOf("\n\n");
    }
  }
}
